import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  HelpCircle, 
  GraduationCap, 
  IndianRupee, 
  TrendingUp, 
  ArrowUpRight, 
  MessageCircle, 
  Clock, 
  Sparkles, 
  Plus,
  RefreshCw,
  ShieldCheck,
  Award
} from 'lucide-react';
import { leadsService, type Lead } from '../../services/leadsService';
import { enquiryService } from '../../services/enquiryService';
import type { Enquiry } from '../../types/enquiry';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';

export function CrmDashboard() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [leadsData, enquiriesData] = await Promise.all([
        leadsService.listLeads(),
        enquiryService.listEnquiries().catch(() => ({ enquiries: [], count: 0 })),
      ]);

      setLeads(leadsData);
      setEnquiries(enquiriesData.enquiries || []);
    } catch (err) {
      console.error('Error loading CRM dashboard:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboardData();
  };

  // Derived metrics
  const totalLeads = leads.length;
  const enrolledCount = leads.filter(l => l.status === 'enrolled').length;
  const registeredCount = leads.filter(l => l.status === 'registered').length;
  const activeFollowups = leads.filter(l => l.status === 'contacted' || l.status === 'counselling').length;
  const newLeadsCount = leads.filter(l => l.status === 'enquired').length;
  
  const totalFeesCollected = leads.reduce((acc, lead) => acc + (lead.paid1 || 0) + (lead.paid2 || 0), 0);
  const totalDueFees = leads.reduce((acc, lead) => acc + (lead.due || 0), 0);

  const conversionRate = totalLeads > 0 ? Math.round((enrolledCount / totalLeads) * 100) : 0;

  // Source distribution
  const sourceStats: Record<string, number> = {
    'WhatsApp Inbound': 0,
    'Meta / Instagram Ads': 0,
    'Google Ads': 0,
    'YouTube': 0,
    'Website': 0,
    'Direct / Other': 0,
  };

  leads.forEach(l => {
    const s = (l.source || '').toLowerCase();
    if (s.includes('whatsapp')) sourceStats['WhatsApp Inbound']++;
    else if (s.includes('meta') || s.includes('facebook') || s.includes('instagram')) sourceStats['Meta / Instagram Ads']++;
    else if (s.includes('google')) sourceStats['Google Ads']++;
    else if (s.includes('youtube')) sourceStats['YouTube']++;
    else if (s.includes('website')) sourceStats['Website']++;
    else sourceStats['Direct / Other']++;
  });

  const sourceChartData = Object.entries(sourceStats).map(([name, value]) => ({
    name,
    value,
  }));

  const COLORS = ['#10B981', '#6366F1', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6'];

  // Pipeline stages chart
  const pipelineData = [
    { stage: 'New / Inbound', count: newLeadsCount, fill: '#3B82F6' },
    { stage: 'Contacted', count: leads.filter(l => l.status === 'contacted').length, fill: '#6366F1' },
    { stage: 'Counseling', count: leads.filter(l => l.status === 'counselling').length, fill: '#8B5CF6' },
    { stage: 'Registered', count: registeredCount, fill: '#EC4899' },
    { stage: 'Enrolled (Admissions)', count: enrolledCount, fill: '#10B981' },
  ];

  // Urgent Follow-ups
  const urgentFollowUps = leads
    .filter(l => l.status !== 'enrolled' && l.status !== 'dropped')
    .slice(0, 5);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Page Title & Quick Action Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800/80 p-5 rounded-2xl shadow-lg backdrop-blur-md">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-white tracking-tight font-heading">
              Admissions & Sales Executive Dashboard
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
              Live Pipeline
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Real-time attribution for WhatsApp, Meta Ads, Google Ads, student counseling, and fee collections.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold border border-slate-700/60 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-indigo-400' : ''}`} />
            Refresh
          </button>

          <Link
            to="/campaign-tools"
            className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-800/60 text-emerald-300 rounded-xl text-xs font-semibold transition-all"
          >
            <MessageCircle className="w-3.5 h-3.5 text-emerald-400" />
            WhatsApp Hub
          </Link>

          <Link
            to="/leads?action=create"
            className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-600/30 transition-all"
          >
            <Plus className="w-4 h-4" />
            New Lead
          </Link>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Leads */}
        <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-lg relative overflow-hidden group hover:border-indigo-500/50 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Inbound Leads</span>
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-3xl font-black text-white font-heading">{totalLeads}</span>
            <span className="inline-flex items-center text-xs font-semibold text-emerald-400">
              <ArrowUpRight className="w-3.5 h-3.5 mr-0.5" />
              Active
            </span>
          </div>
          <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-2">
            <span className="text-indigo-400 font-semibold">{newLeadsCount} New</span>
            <span>•</span>
            <span>{activeFollowups} In Counseling</span>
          </div>
        </div>

        {/* Card 2: Enrolled Admissions */}
        <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-lg relative overflow-hidden group hover:border-emerald-500/50 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Enrolled Admissions</span>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
              <GraduationCap className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-3xl font-black text-white font-heading">{enrolledCount}</span>
            <span className="inline-flex items-center text-xs font-semibold text-emerald-400">
              {conversionRate}% Conv.
            </span>
          </div>
          <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-2">
            <span className="text-emerald-400 font-semibold">{registeredCount} Registered</span>
            <span>•</span>
            <span>Ready for Next Cohort</span>
          </div>
        </div>

        {/* Card 3: Total Collections */}
        <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-lg relative overflow-hidden group hover:border-blue-500/50 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Fees Collected (INR)</span>
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 group-hover:scale-110 transition-transform">
              <IndianRupee className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-2xl font-black text-white font-heading">
              ₹{totalFeesCollected.toLocaleString('en-IN')}
            </span>
            <span className="inline-flex items-center text-xs font-semibold text-emerald-400">
              Paid
            </span>
          </div>
          <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-2">
            <span>Outstanding: </span>
            <span className="text-amber-400 font-semibold">₹{totalDueFees.toLocaleString('en-IN')}</span>
          </div>
        </div>

        {/* Card 4: Inquiries */}
        <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-lg relative overflow-hidden group hover:border-purple-500/50 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Course Enquiries</span>
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform">
              <HelpCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-3xl font-black text-white font-heading">{enquiries.length}</span>
            <span className="inline-flex items-center text-xs font-semibold text-purple-400">
              Desk Active
            </span>
          </div>
          <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-2">
            <span className="text-purple-400 font-semibold">{enquiries.filter(e => e.status === 'new').length} Unhandled</span>
            <span>•</span>
            <Link to="/enquiries" className="text-indigo-400 hover:underline">View Desk</Link>
          </div>
        </div>
      </div>

      {/* Analytics Section: Pipeline Stages & Marketing Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Distribution */}
        <div className="lg:col-span-2 bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold text-white font-heading flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-indigo-400" />
                Sales Pipeline Funnel
              </h2>
              <p className="text-xs text-slate-400">Real-time candidate progression through conversion stages</p>
            </div>
            <Link to="/leads" className="text-xs font-semibold text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
              Kanban View <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipelineData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                <XAxis type="number" stroke="#64748B" fontSize={11} />
                <YAxis dataKey="stage" type="category" stroke="#94A3B8" fontSize={11} width={130} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0F172A', borderColor: '#334155', borderRadius: '8px', color: '#F8FAFC', fontSize: '12px' }}
                />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                  {pipelineData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Marketing Attribution Chart */}
        <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-white font-heading flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-400" />
                Lead Sources
              </h2>
              <span className="text-[11px] text-slate-400">Attribution</span>
            </div>
            <p className="text-xs text-slate-400 mb-2">Campaign tracking from WhatsApp tags and ads</p>
          </div>

          <div className="h-44 w-full flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sourceChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={65}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {sourceChartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0F172A', borderColor: '#334155', borderRadius: '8px', color: '#F8FAFC', fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-1.5 mt-2">
            {sourceChartData.slice(0, 4).map((s, idx) => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-slate-300 truncate">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                  {s.name}
                </span>
                <span className="font-bold text-white font-mono">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Section: Urgent Follow-ups & Quick Action Shortcuts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Urgent Follow-ups Table */}
        <div className="lg:col-span-2 bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold text-white font-heading flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-400" />
                Active Counseling & Follow-ups
              </h2>
              <p className="text-xs text-slate-400">Leads awaiting counselor callback or demo session</p>
            </div>
            <Link to="/leads" className="text-xs font-semibold text-indigo-400 hover:text-indigo-300">
              View All Leads →
            </Link>
          </div>

          {urgentFollowUps.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-xs">
              No pending follow-ups right now. All caught up!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-300">
                <thead className="bg-slate-950/60 text-slate-400 uppercase tracking-wider text-[10px] border-b border-slate-800">
                  <tr>
                    <th className="p-3">Candidate</th>
                    <th className="p-3">Program</th>
                    <th className="p-3">Source</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 font-medium">
                  {urgentFollowUps.map((lead) => (
                    <tr key={lead.leadId} className="hover:bg-slate-800/40 transition-colors">
                      <td className="p-3">
                        <div className="font-bold text-white">{lead.name}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{lead.phone}</div>
                      </td>
                      <td className="p-3 text-slate-300 truncate max-w-[150px]">
                        {lead.courseInterest || 'FDE Program'}
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-800 text-slate-300 border border-slate-700">
                          {lead.source}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                          {lead.status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <a
                          href={`https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-950/80 hover:bg-emerald-900 text-emerald-400 border border-emerald-800/60 rounded-lg text-[11px] font-semibold transition-all"
                        >
                          <MessageCircle className="w-3 h-3" />
                          Chat
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Tools & Hotlinks */}
        <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-5 shadow-lg space-y-4">
          <h2 className="text-base font-bold text-white font-heading flex items-center gap-2">
            <Award className="w-4 h-4 text-indigo-400" />
            CRM Shortcuts
          </h2>

          <div className="space-y-2.5">
            <Link
              to="/fees"
              className="flex items-center justify-between p-3 rounded-xl bg-slate-950/70 border border-slate-800 hover:border-indigo-500/50 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center">
                  <IndianRupee className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs font-bold text-white group-hover:text-indigo-300">Generate Fee Receipt</div>
                  <div className="text-[10px] text-slate-400">Download official PDF invoice</div>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-white" />
            </Link>

            <Link
              to="/admissions"
              className="flex items-center justify-between p-3 rounded-xl bg-slate-950/70 border border-slate-800 hover:border-emerald-500/50 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
                  <GraduationCap className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs font-bold text-white group-hover:text-emerald-300">Cohort Admissions</div>
                  <div className="text-[10px] text-slate-400">Assign batches & mentors</div>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-white" />
            </Link>

            <Link
              to="/campaign-tools"
              className="flex items-center justify-between p-3 rounded-xl bg-slate-950/70 border border-slate-800 hover:border-purple-500/50 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs font-bold text-white group-hover:text-purple-300">Campaign UTM Builder</div>
                  <div className="text-[10px] text-slate-400">Meta, Google & YouTube links</div>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-white" />
            </Link>
          </div>

          <div className="p-3 bg-indigo-950/40 border border-indigo-900/50 rounded-xl">
            <div className="flex items-center gap-2 text-indigo-300 text-xs font-bold mb-1">
              <ShieldCheck className="w-4 h-4 text-indigo-400" />
              AWS Infrastructure
            </div>
            <p className="text-[11px] text-indigo-200/80 leading-relaxed">
              Connected to API Gateway, DynamoDB (<span className="font-mono text-indigo-300">CloudBinaryLMS-prod-Leads</span>), and Meta Webhook.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
